/**
 * Plan 20a, Task 4.2 — Countdown zoom + podium celebration hooks
 *
 * Two hook factories share this module (imports + UiVfxHookConfig) but have
 * NO shared state. Each factory instance manages its own in-flight handle.
 *
 * ── createCountdownZoomHook ──────────────────────────────────────────────────
 *
 * Factory: createCountdownZoomHook(config: UiVfxHookConfig)
 *   Returns a UiVfxHook<HTMLElement, { number: '3' | '2' | '1' | 'GO!' }> with
 *   id 'countdown-zoom'.
 *
 * '3' | '2' | '1': scale 2.5→1.0 + opacity 0→1 over 500ms bouncy; hold 400ms;
 *   fade out 150ms. Total ~1050ms.
 *
 * 'GO!': scale 3.0→1.0 with extra bounce; hold 600ms; full-screen white flash
 *   on sibling [data-flash-overlay] (peak 20% opacity, 200ms) starting at 0.5s.
 *
 * ── createPodiumCelebrationHook ──────────────────────────────────────────────
 *
 * Factory: createPodiumCelebrationHook(config: UiVfxHookConfig)
 *   Returns a UiVfxHook<HTMLElement, { placement: 1 | 2 | 3 | 4 }> with
 *   id 'podium-celebration'.
 *
 * placement 1: scale 0.8→1.1 (125ms bouncy) + settle 1.1→1.0 (125ms) +
 *   box-shadow pulse via SELF-CONTAINED child timeline (repeat:-1, yoyo:true).
 *   The child timeline is created via gsap.timeline({ repeat:-1, yoyo:true })
 *   and added to the registry timeline via tl.add(pulseTl, 0.25). This ensures
 *   the child's repeat operates on its OWN natural duration (~0.5s), NOT the
 *   parent's 3600s sentinel-extended duration. Without the child-timeline isolation,
 *   repeat:-1.yoyo:true on the parent would produce a ~7200s effective loop period
 *   (HARD-1 bug fix — quality reviewer commit 923513ab).
 *
 * placement 2 | 3 | 4: opacity 0→1 + scale 0.8→1.0 over 250ms (no repeat).
 *   Color (silver/bronze/neutral) handled by CSS class on label.
 *
 * NOTE — completion callbacks:
 *   The gsap-registry sentinel tween extends every timeline to VIRTUAL_DURATION=3600s.
 *   eventCallback('onComplete') fires at 3600s, not at the natural animation end.
 *   Use timeline.call(fn, undefined, absoluteSeconds) for callbacks.
 *   Canonical reference: src/client/renderer/animation/hit-reaction.ts:17-18.
 *
 * NOTE — explicit position args (class-wide fix 5240f572):
 *   Every tl.to/fromTo/call MUST include an explicit position arg (4th/3rd param).
 *   Omitting it causes GSAP to append at the timeline end (3600 due to sentinel),
 *   where the tween never fires within normal game time.
 */

import gsap from 'gsap';
import type { UiVfxHook, UiVfxHookConfig } from './types';
import type { GsapTimelineHandle } from '../../renderer/vfx/gsap-registry';

// ─────────────────────────────────────────────────────────────────────────────
// Public opts types
// ─────────────────────────────────────────────────────────────────────────────

export interface CountdownZoomOpts {
  number: '3' | '2' | '1' | 'GO!';
}

export interface PodiumCelebrationOpts {
  placement: 1 | 2 | 3 | 4;
}

// ─────────────────────────────────────────────────────────────────────────────
// Countdown Zoom Hook
// ─────────────────────────────────────────────────────────────────────────────

export function createCountdownZoomHook(
  config: UiVfxHookConfig,
): UiVfxHook<HTMLElement, CountdownZoomOpts> {
  const { gsapRegistry } = config;

  /** In-flight handle; at most one active at a time. */
  let currentHandle: GsapTimelineHandle | null = null;

  /** Kill the current in-flight timeline, if any. */
  function killCurrent(): void {
    if (currentHandle) {
      currentHandle.dispose();
      currentHandle = null;
    }
  }

  function play(target: HTMLElement, opts?: CountdownZoomOpts): GsapTimelineHandle {
    // Kill any prior timeline before spawning a new one.
    killCurrent();

    const number = opts?.number ?? '3';

    // For 'GO!': look for [data-flash-overlay] in the parent container (target may be
    // the stage root containing the countdown el + flash overlay as siblings, OR target
    // may be the countdown el itself whose parent has the flash overlay).
    // We query from target first, then from its parentElement, then from document.
    const flashOverlay: HTMLElement | null =
      (target.querySelector('[data-flash-overlay]') as HTMLElement | null) ??
      (target.parentElement?.querySelector('[data-flash-overlay]') as HTMLElement | null);

    const handle = gsapRegistry.create(`countdown-zoom:${number}`);
    const tl = handle.timeline;

    if (number === 'GO!') {
      // ── Phase 1 (0–500ms): scale 3.0→1.0 + opacity 0→1 with extra bounce ─
      tl.fromTo(
        target,
        { scale: 3.0, opacity: 0 },
        { scale: 1.0, opacity: 1, duration: 0.5, ease: 'back.out(3.5)' },
        0, // ← explicit position: 0s
      );

      // ── Phase 2 (500–700ms): white flash on [data-flash-overlay] ──────────
      // Ramp up: opacity 0→0.2 over 100ms starting at t=0.5
      if (flashOverlay) {
        tl.fromTo(
          flashOverlay,
          { opacity: 0 },
          { opacity: 0.2, duration: 0.1, ease: 'power2.out' },
          0.5, // ← explicit position: 0.5s
        );

        // Ramp down: opacity 0.2→0 over 100ms starting at t=0.6
        tl.to(
          flashOverlay,
          { opacity: 0, duration: 0.1, ease: 'power2.in' },
          0.6, // ← explicit position: 0.6s
        );
      }

      // ── Phase 3: hold 600ms (0.5s–1.1s), then fade out 150ms (1.1s–1.25s) ──
      // HARD-2 fix: the prior implementation falsely claimed that the 200ms flash
      // phase (0.5–0.7s) constituted the 600ms hold. Actual hold = 0s of hold.
      //
      // Spec (components.md §507 + plan line 379): 'GO!' holds 600ms after scale-in.
      // Timeline: 0–0.5s zoom; 0.5–0.7s flash (overlaps with hold start);
      //           0.7–1.1s implicit hold (no tween, 400ms remaining = total 600ms);
      //           1.1–1.25s fade-out opacity 1→0 (150ms, matches '3'/'2'/'1' pattern).
      //
      // Total natural duration: 1.25s.
      // Matches the '3'/'2'/'1' branch which has explicit hold + fade-out.
      tl.to(
        target,
        { opacity: 0, duration: 0.15, ease: 'power2.in' },
        1.1, // ← explicit position: 1.1s (after 600ms hold: 0.5s scale-end + 0.6s hold)
      );
    } else {
      // '3' | '2' | '1'

      // ── Phase 1 (0–500ms): scale 2.5→1.0 + opacity 0→1 with bouncy ease ──
      tl.fromTo(
        target,
        { scale: 2.5, opacity: 0 },
        { scale: 1.0, opacity: 1, duration: 0.5, ease: 'back.out(2.5)' },
        0, // ← explicit position: 0s
      );

      // ── Phase 2: hold 400ms (no tween; 500–900ms) ─────────────────────────
      // Hold is implicit — no tween in that window.

      // ── Phase 3 (900–1050ms): fade out opacity 1→0 over 150ms ────────────
      tl.to(
        target,
        { opacity: 0, duration: 0.15, ease: 'power2.in' },
        0.9, // ← explicit position: 0.9s
      );
    }

    currentHandle = handle;
    return handle;
  }

  function dispose(): void {
    killCurrent();
  }

  return {
    id: 'countdown-zoom',
    play,
    dispose,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Podium Celebration Hook
// ─────────────────────────────────────────────────────────────────────────────

export function createPodiumCelebrationHook(
  config: UiVfxHookConfig,
): UiVfxHook<HTMLElement, PodiumCelebrationOpts> {
  const { gsapRegistry } = config;

  /** In-flight handle; at most one active at a time. */
  let currentHandle: GsapTimelineHandle | null = null;

  /** Kill the current in-flight timeline, if any. */
  function killCurrent(): void {
    if (currentHandle) {
      currentHandle.dispose();
      currentHandle = null;
    }
  }

  function play(target: HTMLElement, opts?: PodiumCelebrationOpts): GsapTimelineHandle {
    // Kill any prior timeline before spawning a new one.
    killCurrent();

    const placement = opts?.placement ?? 1;

    const handle = gsapRegistry.create(`podium-celebration:${placement}`);
    const tl = handle.timeline;

    if (placement === 1) {
      // ── Scale up: 0.8→1.1 over 125ms bouncy (t=0) ────────────────────────
      tl.fromTo(
        target,
        { scale: 0.8 },
        { scale: 1.1, duration: 0.125, ease: 'back.out(2.5)' },
        0, // ← explicit position: 0s
      );

      // ── Settle: 1.1→1.0 over 125ms (t=0.125) ─────────────────────────────
      tl.to(
        target,
        { scale: 1.0, duration: 0.125, ease: 'power2.out' },
        0.125, // ← explicit position: 0.125s
      );

      // ── Box-shadow pulse: 12px↔24px gold (SELF-CONTAINED child timeline) ──
      // HARD-1 fix: do NOT apply repeat:-1.yoyo:true to the registry-managed
      // parent timeline. The parent's sentinel tween at 3600s makes its effective
      // duration 3600s, so repeat:-1.yoyo:true on the parent produces a ~7200s
      // loop period (pop + static gold for 3599s, then reverse for 3599s).
      //
      // Fix: create a separate child timeline NOT managed by the registry.
      // The child's natural duration is its own tween duration (~0.5s), so
      // repeat:-1.yoyo:true produces a ~1s pulse cycle — continuously visible.
      // The child is paused:false (runs on GSAP's auto-ticker) and attached to
      // the parent via tl.add(pulseTl, 0.25) so its lifecycle follows the parent.
      //
      // Spec (components.md §507 + plan line 384): '0 0 0 transparent → 0 0 24px
      // gold → 0 0 12px gold' — implementing as 12px↔24px oscillation (subtle
      // settle pattern) rather than the prior dramatic transparent↔24px flash.
      const pulseTl = gsap.timeline({ repeat: -1, yoyo: true });
      pulseTl.fromTo(
        target,
        { boxShadow: '0 0 12px 0px #FFD700' },
        { boxShadow: '0 0 24px 0px #FFD700', duration: 0.5, ease: 'sine.inOut' },
      );

      tl.add(pulseTl, 0.25); // ← start pulse after the scale pop settles
    } else {
      // placement 2 | 3 | 4 — simple fade-in + scale, no sustained pulse

      // ── Fade in + scale: 0.8→1.0 + opacity 0→1 over 250ms ────────────────
      tl.fromTo(
        target,
        { opacity: 0, scale: 0.8 },
        { opacity: 1, scale: 1.0, duration: 0.25, ease: 'power2.out' },
        0, // ← explicit position: 0s
      );
      // No repeat — color handled by CSS class on the label.
    }

    currentHandle = handle;
    return handle;
  }

  function dispose(): void {
    killCurrent();
  }

  return {
    id: 'podium-celebration',
    play,
    dispose,
  };
}
